import { useEffect } from 'react'
import { useParams } from 'router-v6'
import { getPubnubTimetoken } from 'v2/pages/conversations/utils'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { usePubNub } from 'v2/shared/hooks/use-pubnub'
import { usePubnubMutations } from 'v2/shared/hooks/use-pubnub.mutations'
import { playgroundService } from 'v2/shared/services/playground-service'
import { getEmbedSession, isEmbedMode } from 'v2/shared/utils/embed-session'
import { FlowTypes } from 'v2/types'
import { PubnubEvents } from 'v2/types/pubnub'
import { getLocalAuth } from 'v2/utils'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { agentLogger } from 'v2/utils/logger'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { type PlaygroundStream, playgroundStream } from 'v2/utils/playground-stream'
import { errorToast } from 'v2/utils/toasts'
import { usePlaygroundStreamTransport } from '../../hooks/use-playground-stream-transport'
import { useFlowStore } from '../../store'
import { useFlowRunStore } from '../store'
import type { FlowRunEvent } from '../types'
import { highlightNodeAndEdges } from '../utils'

// The local isFlowRunning flips before the run reaches the backend, so the
// stream open waits for the runner to publish its mapping. Bounded so a run that
// never starts (trigger error) stops polling instead of retrying forever.
export const RUN_CONFIRM_POLL_MS = 400
export const RUN_CONFIRM_MAX_TRIES = 25

// Pure decision helpers, exported so they can be tested directly. The hook
// itself reads their inputs from React context, and context-derived flags are
// leak-fragile in this suite (CLAUDE.md failure mode 17) — these tests are
// leak-immune and cover the same decisions.
export const shouldOpenStream = (input: {
  isInitialData?: boolean
  streamTransportEnabled: boolean
  phloId?: string
  isFlowRunning: boolean
}): boolean =>
  !input.isInitialData &&
  input.streamTransportEnabled &&
  Boolean(input.phloId) &&
  input.isFlowRunning

// The stream transport takes the LATEST announcement: the bootstrap replay
// delivers up to two hours of them oldest-first, and currentRunId is what
// replies are routed to. PubNub keeps first-wins, where the same guard also
// prevents a duplicate per-run subscribe.
export const shouldReplaceRunId = (
  streamTransportEnabled: boolean,
  currentRunId: string | null,
): boolean => streamTransportEnabled || !currentRunId

// Embed sessions share one actor channel, so adopt only runs carrying this session's realtime_ref.
export const shouldAdoptRunAnnouncement = (
  embed: boolean,
  announcedSessionRef: string | undefined,
  myRealtimeRef: string | undefined,
): boolean => !embed || (!!announcedSessionRef && announcedSessionRef === myRealtimeRef)

export const useRunEvents = () => {
  const { pubnubRef } = usePubNub()
  const { subscribeMutation, unsubscribeMutation } = usePubnubMutations({ pubnubRef })
  const { isInitialData } = useFeatureFlags()
  const playgroundStreamTransportEnabled = usePlaygroundStreamTransport()
  const setIsFlowRunning = useFlowRunStore((state) => state.setIsFlowRunning)
  const isFlowRunning = useFlowRunStore((state) => state.isFlowRunning)
  const { id: phloId } = useParams()

  const unsubscribeFromPlaygroundChannel = () => {
    const playgroundChannel = pubnubRef.current?.pubnub
      .getSubscribedChannels()
      .find((channel) => channel.includes('playground'))
    if (playgroundChannel) {
      unsubscribeMutation.mutate([playgroundChannel])
    }
    phoneSDK.stopNoiseReduction()
    setIsFlowRunning(false)
    // This is to remove highlight from any nodes
    highlightNodeAndEdges([], [])
  }

  // Event-bus handlers, bound for the whole builder-tab lifetime and shared by
  // both transports — the stream's onEvent re-emits onto this same bus, so
  // replayed and live frames land on the handlers below either way.
  useEffect(() => {
    const unbinds = [
      pubnubEventBus.on(PubnubEvents.PLAYGROUND_INITIATED, (data: unknown) => {
        const payload = data as Record<string, string>
        const embed = isEmbedMode()
        if (
          !shouldAdoptRunAnnouncement(embed, payload.session_ref, getEmbedSession()?.realtime_ref)
        ) {
          return
        }

        const existingRunId = useFlowRunStore.getState().currentRunId
        if (!shouldReplaceRunId(playgroundStreamTransportEnabled, existingRunId)) return

        if (!playgroundStreamTransportEnabled) {
          subscribeMutation.mutate({
            channels: [payload.playground_channel_name],
            timetoken: getPubnubTimetoken(Date.now() - 20000), // Increased buffer to 20 seconds to ensure we don't miss any messages
          })
        }

        // Stream transport: LATEST announcement wins. The bootstrap stream
        // replays every announcement of the last two hours oldest-first, so
        // first-wins would pin currentRunId to a finished run — and
        // currentRunId is what use-send-message routes replies to, which would
        // send them into a dead run. The stream is only opened once the runner
        // confirms our run is the mapped one, so the last replayed announcement
        // is the current run.
        useFlowRunStore.getState().setCurrentRunId(payload.playground_channel_name)
      }),

      // Helper function to add events to flow run store
      ...(
        [
          PubnubEvents.NODE_EXECUTED,
          PubnubEvents.NODE_UPDATED,
          PubnubEvents.NODE_CHUNK_CREATED,
          PubnubEvents.SYSTEM_CHUNK_CREATED,
        ] as const
      ).map((eventName) =>
        pubnubEventBus.on(eventName, (data: FlowRunEvent) => {
          useFlowRunStore.getState().addEvent({
            ...data,
            event_name: eventName,
          } as FlowRunEvent)
        }),
      ),

      pubnubEventBus.on(PubnubEvents.FLOW_EXECUTION_COMPLETE, (data) => {
        useFlowRunStore.getState().addEvent({
          ...data,
          event_name: PubnubEvents.FLOW_EXECUTION_COMPLETE,
        })

        unsubscribeFromPlaygroundChannel()
        // The stream effect closes on this falling edge.
        useFlowRunStore.getState().setIsFlowRunning(false)
      }),

      primaryEventBus.on(PrimaryEvents.HANGUP_CALL, () => {
        useFlowRunStore.getState().reset()
        phoneSDK.stopNoiseReduction()
      }),

      primaryEventBus.on(PrimaryEvents.CALL_FAILED, (data) => {
        if (data.callInfo.reason === 'Rejected') return
        unsubscribeFromPlaygroundChannel()
      }),
    ]

    return () => {
      unbinds.forEach((unbind) => unbind())
      unsubscribeFromPlaygroundChannel()
    }
  }, [playgroundStreamTransportEnabled])

  // The event stream, opened only while a run is actually in flight — never on
  // bare render, so an idle builder tab holds no connection. The open is
  // deferred past the local isFlowRunning edge until the runner confirms the
  // run, because the server replays the bootstrap stream from the start and
  // would otherwise attach whichever run it last announced.
  useEffect(() => {
    // Checked separately as well as inside the helper, so phloId narrows to a
    // string for the calls below — TypeScript cannot see through the helper.
    if (!phloId) return
    if (
      !shouldOpenStream({
        isInitialData,
        streamTransportEnabled: playgroundStreamTransportEnabled,
        phloId,
        isFlowRunning,
      })
    ) {
      return
    }

    const aomUuid = getLocalAuth().aom_uuid
    let cancelled = false
    let stream: PlaygroundStream | null = null
    let timer: ReturnType<typeof setTimeout> | null = null

    const openWhenConfirmed = async (triesLeft: number) => {
      if (cancelled || stream) return
      let inFlight = false
      try {
        inFlight = (await playgroundService.getStatus(aomUuid, phloId)).in_flight
      } catch {
        inFlight = false
      }
      if (cancelled || stream) return
      if (inFlight) {
        stream = playgroundStream.open(aomUuid, phloId, {
          onEvent: (body) =>
            pubnubEventBus.emit(body.content.eventName as PubnubEvents, body.content.eventData),
          onError: (_error, info) => {
            if (!info?.terminal) return
            // The stream is gone for good, so no completion event can arrive to
            // clear this — without it the panel shows a dead run as active
            // forever. The falling edge also runs this effect's cleanup, which
            // closes the (already-closed) stream.
            useFlowRunStore.getState().setIsFlowRunning(false)
            // 401/403: the stream stopped for good — a dead session must not
            // look like a quiet agent.
            errorToast({
              title: 'Playground disconnected',
              description: 'Your session no longer has access. Reload the page to reconnect.',
            })
          },
        })
      } else if (triesLeft > 0) {
        timer = setTimeout(() => void openWhenConfirmed(triesLeft - 1), RUN_CONFIRM_POLL_MS)
      } else {
        // The runner never confirmed the run inside the poll budget — without
        // this the user watches a spinner forever with no stream and no error.
        agentLogger.error('playground run never confirmed, giving up', { phloId })
        errorToast({
          title: 'Playground did not start',
          description: 'The run was not confirmed in time. Stop and run the test again.',
        })
        useFlowRunStore.getState().setIsFlowRunning(false)
      }
    }

    void openWhenConfirmed(RUN_CONFIRM_MAX_TRIES)

    // Cleanup closes the stream only — no store writes, or the falling edge
    // would fight the handlers above.
    return () => {
      cancelled = true
      if (timer) {
        clearTimeout(timer)
      }
      stream?.close()
    }
  }, [isFlowRunning, isInitialData, playgroundStreamTransportEnabled, phloId])

  // Resume after a reload or deep link. Neither run store survives a reload, so
  // the server is the only record that a run is still live; arming
  // isFlowRunning lets the effect above open the stream and replay the run, and
  // lets addEvent render it (it drops events while the flag is false).
  useEffect(() => {
    if (isInitialData || !playgroundStreamTransportEnabled || !phloId) return
    if (useFlowRunStore.getState().isFlowRunning) return
    // Chat playground deliberately stays on the PubNub chat stack, and its
    // reply path needs asyncAgentMeta (chatChannel, from/to), which no store
    // survives a reload. Arming it here would render the transcript but leave
    // the composer broken: with asyncAgentMeta empty, channelName is undefined,
    // so a reply takes the stream branch, fails the forwarder's conv_channel
    // check and 400s. Leave chat un-resumed — that is its pre-flag behaviour.
    // currentFlowType is set from the same flowData this panel waits on, so it
    // is already populated by the time this effect runs.
    if (useFlowStore.getState().currentFlowType === FlowTypes.incoming_chat) return

    let cancelled = false
    playgroundService
      .getStatus(getLocalAuth().aom_uuid, phloId)
      .then((status) => {
        if (cancelled || !status.in_flight) return
        // Adopt the run id BEFORE arming isFlowRunning. /status already knows
        // it, and the composer routes replies by currentRunId — arming first
        // would let the user send into `undefined` (treated as message #1,
        // which starts a NEW run) during the window before the replayed
        // announcement lands. Message #1 legitimately has no run id; a resumed
        // run always does.
        if (status.phlo_run_uuid) {
          // Store the CHANNEL, not the bare uuid: currentRunId goes out as the
          // POST `channel`, and QES demuxes playground callbacks on
          // HasPrefix(channel, "playground") (chat/controller.go:162). A bare
          // uuid lands in the chat branch, finds no state, and is dropped with
          // a 200. The live path stores payload.playground_channel_name, which
          // is this same string.
          useFlowRunStore.getState().setCurrentRunId(`playground.${status.phlo_run_uuid}`)
        }
        useFlowRunStore.getState().setIsFlowRunning(true)
      })
      .catch((error: unknown) => {
        // Swallowing this hid a dead resume path entirely.
        agentLogger.error('playground resume status check failed', error)
      })

    return () => {
      cancelled = true
    }
  }, [isInitialData, playgroundStreamTransportEnabled, phloId])
}
